// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { Extension } from '@medplum/fhirtypes';
import { FHIR_TIMEZONE_EXTENSION_URL } from './constants';

const FHIR_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;
const EXPLICIT_OFFSET_PATTERN = /(Z|[+-]\d{2}:\d{2})$/;

function validateFhirDateTimeGrammar(value: string): RegExpMatchArray {
  const match = value.match(FHIR_DATE_TIME_PATTERN);
  if (!match) {
    throw new Error(`Invalid FHIR dateTime grammar: ${value}`);
  }

  const [, yearText, monthText, dayText, hourText, minuteText, secondText, , offset] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const second = Number(secondText);
  const offsetHours = offset === 'Z' ? 0 : Number(offset.slice(1, 3));
  const offsetMinutes = offset === 'Z' ? 0 : Number(offset.slice(4, 6));
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();

  if (
    year < 1 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth ||
    hour > 23 ||
    minute > 59 ||
    second > 59 ||
    offsetHours > 14 ||
    offsetMinutes > 59 ||
    (offsetHours === 14 && offsetMinutes !== 0)
  ) {
    throw new Error(`Invalid FHIR dateTime components: ${value}`);
  }

  return match;
}

function getDateParts(date: Date, timezone: string): Record<string, string> {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  return Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
}

function getFraction(value: Date | string, date: Date): string {
  if (typeof value === 'string') {
    return value.match(FHIR_DATE_TIME_PATTERN)?.[7] ?? '';
  }
  if (date.getMilliseconds() === 0) {
    return '';
  }
  return `.${date.getMilliseconds().toString().padStart(3, '0')}`;
}

export function isValidTimezone(timezone: string | undefined): boolean {
  if (!timezone) {
    return false;
  }
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format();
    return true;
  } catch {
    return false;
  }
}

export function assertValidTimezone(timezone: string): string {
  if (!isValidTimezone(timezone)) {
    throw new Error(`Invalid scheduling timezone: ${timezone}`);
  }
  return timezone;
}

export function hasExplicitTimezoneOffset(value: string): boolean {
  return EXPLICIT_OFFSET_PATTERN.test(value);
}

export function getTimezoneExtensionValue(extensions: Extension[] | undefined): string | undefined {
  const timezoneExtensions = (extensions ?? []).filter((extension) => extension.url === FHIR_TIMEZONE_EXTENSION_URL);
  if (timezoneExtensions.length !== 1) {
    return undefined;
  }

  const extension = timezoneExtensions[0];
  const values = [extension.valueCode, extension.valueString].filter(
    (value): value is string => typeof value === 'string'
  );
  const timezone = values.length === 1 ? values[0] : undefined;
  return timezone && isValidTimezone(timezone) ? timezone : undefined;
}

export function parseFhirDateTime(value: string): Date {
  if (typeof value !== 'string' || !hasExplicitTimezoneOffset(value)) {
    throw new Error(`FHIR dateTime must include an explicit timezone offset: ${value}`);
  }
  validateFhirDateTimeGrammar(value);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid FHIR dateTime: ${value}`);
  }
  return date;
}

export function formatFhirDateTimeInTimezone(value: Date | string, timezone: string): string {
  const date = typeof value === 'string' ? parseFhirDateTime(value) : value;
  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid date cannot be formatted as a FHIR dateTime');
  }
  assertValidTimezone(timezone);
  const parts = getDateParts(date, timezone);
  const localAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
  const offsetMinutes = Math.round((localAsUtc - date.getTime()) / 60000);
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteOffset = Math.abs(offsetMinutes);
  const offsetHours = Math.floor(absoluteOffset / 60)
    .toString()
    .padStart(2, '0');
  const offsetMinutesPart = (absoluteOffset % 60).toString().padStart(2, '0');

  const fraction = getFraction(value, date);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}${fraction}${sign}${offsetHours}:${offsetMinutesPart}`;
}
